import { normalizeResidentSortType } from '@/utils/residentSort';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../core/store';
import { readResidentCategories } from '@/utils/residentCategories';
import { SortType } from '@/types/components';
import { DFREE_GRAPH_VIEW_TYPE, DfreeGraphViewType } from '@/constants';

interface ResidentListSettings {
  sortType: SortType;
  categories: string[];
  viewType: DfreeGraphViewType;
}

const initialState: ResidentListSettings = {
  sortType: normalizeResidentSortType(localStorage.getItem('sort')),
  categories: readResidentCategories(),
  viewType: getInitialState<DfreeGraphViewType>('view', DFREE_GRAPH_VIEW_TYPE.Volume),
};

export const residentListSettingsSlice = createSlice({
  name: 'residentListSettings',
  initialState,
  reducers: {
    setSortType: (state: ResidentListSettings, action: PayloadAction<ResidentListSettings['sortType']>) => {
      state.sortType = action.payload;
    },
    setFilterCondition: (
      state: ResidentListSettings,
      action: PayloadAction<Omit<ResidentListSettings, 'sortType' | 'viewType'>>
    ) => {
      state.categories = action.payload.categories;
    },
    setViewType: (state: ResidentListSettings, action: PayloadAction<ResidentListSettings['viewType']>) => {
      state.viewType = action.payload;
    },
    set: (state: ResidentListSettings, action: PayloadAction<ResidentListSettings>) => {
      state.sortType = action.payload.sortType;
      state.categories = action.payload.categories;
    },
    resetSortType: (state: ResidentListSettings) => {
      state.sortType = 'unit';
    },
    resetFilterCondition: (state: ResidentListSettings) => {
      state.categories = [];
    },
    reset: (state: ResidentListSettings) => {
      state.sortType = 'unit';
      state.categories = [];
      state.viewType = DFREE_GRAPH_VIEW_TYPE.Volume;
    },
  },
});

export const { setSortType, setFilterCondition, set, resetSortType, resetFilterCondition, reset } =
  residentListSettingsSlice.actions;

export const residentListSettingsSelector = (state: RootState) => state.residentListSettings;
export const filterConditionSelector = (state: RootState) => ({
  categories: state.residentListSettings.categories,
});
export const sortTypeSelector = (state: RootState) => ({
  sortType: state.residentListSettings.sortType,
});

function getInitialState<T>(key: string, defaultValue: T): T {
  const storedValue = localStorage.getItem(key);
  if (storedValue === null) return defaultValue;
  return typeof defaultValue === 'string' ? (storedValue as unknown as T) : JSON.parse(storedValue);
}
